import { useContext } from 'react';
import { DiscountHeader } from './DiscountHeader';
import { DiscountInput } from './DiscountInput';
import { DiscountOverview } from './DiscountOverview';
import CartContext from '../../contexts/CartContext';
import { useDiscounts } from '../../hooks/useDiscounts';
import { DiscountBadge } from '../elements/DiscountBadge';
import { Banner } from '../elements/Banner';
import { ReactComponent as InfoIcon } from '../../common/icons/info.svg';
import { ScreenType, Settings } from '../../domain';
import { getDiscount, getTotalSpending } from '../../utils/discount';
import classNames from 'classnames';
import isMobile from '../../common/utils/isMobile';

interface DiscountScreenProps {
  discountCode: string;
  onDiscountCodeChanged: (code: string) => void;
  setCurrentScreen: (screen: ScreenType) => void;
  settings: Settings;
  onToggle: () => void;
}

export function DiscountScreen({
  discountCode,
  onDiscountCodeChanged,
  setCurrentScreen,
  settings,
  onToggle,
}: DiscountScreenProps) {
  const {
    handleSubmit,
    handleStepBack,
    discountState,
    handleChange,
    inputValue,
    handleCancel,
    isLabelShrink,
    setIsLabelShrink,
  } = useDiscounts(discountCode, onDiscountCodeChanged, setCurrentScreen);

  const { cart } = useContext(CartContext);

  const totalSpending: number = getTotalSpending(cart);
  const discount: number = getDiscount(cart, totalSpending);
  const subtotalPrice: number = totalSpending + discount;

  return (
    <form
      onSubmit={handleSubmit}
      className={classNames('Giftbox-DiscountScreenContent py-6 px-5', {
        'pt-2': isMobile(),
      })}
    >
      <DiscountHeader
        title={settings?.discount_page_header ?? ''}
        onStepBack={handleStepBack}
        onClose={onToggle}
      />

      <DiscountInput
        className="mt-3.5"
        value={inputValue}
        onChange={handleChange}
        errorMessage={
          discountState === 'error'
            ? settings.discount_incorrect_info
            : undefined
        }
        isLoading={discountState === 'loading'}
        isLabelShrink={isLabelShrink}
        setIsLabelShrink={setIsLabelShrink}
        onEnterPress={handleSubmit}
      />

      <div className="DiscountScreen-Overview my-4">
        {discountCode ? (
          <>
            <DiscountBadge
              discountCode={discountCode}
              onRemove={handleCancel}
              className="mb-4"
            />
            <DiscountOverview
              className="text-gray-900"
              discountCode={discountCode}
              subtotalPrice={subtotalPrice}
              discountPrice={discount}
              totalPrice={totalSpending ?? 0}
            />
          </>
        ) : null}
      </div>

      {discountState === 'success' ? (
        <Banner
          icon={<InfoIcon />}
          text={settings?.discount_correct_info ?? ''}
          className="DiscountScreen-Banner mt-4 text-gray-900"
        />
      ) : null}
    </form>
  );
}
